import { computed, type Ref } from 'vue'
import type { MultiselectOption } from '@ironsight/types'
import { noLocationOption } from '@/components/locations/useLocationResolver'
import { HeadlessAutoCompleteValueType } from '@/components/vision/next/inputs/useAutoComplete'
import type { SelectAllScope } from '@/components/jobs/job-search-page/useFilterChips'
import { useSelectAllSentinel } from '@/components/jobs/job-search-page/useSelectAllSentinel'

type ReadonlyRef<T> = {
  readonly value: T
}

type LocationTypeFilterValue = {
  id?: number | null
  label?: string
}

export type LocationPickerItem = HeadlessAutoCompleteValueType & {
  locationTypeId?: number | null
  parentId?: number | null
}

type UseLocationSelectAllArgs = {
  value: () => unknown
  searchText: Ref<string>
  selectedLocationIds: Ref<Array<number | null>>
  items: ReadonlyRef<HeadlessAutoCompleteValueType[]>
  showNoLocationOption: ReadonlyRef<boolean>
  locationType: ReadonlyRef<LocationTypeFilterValue | null | undefined>
  parentName: ReadonlyRef<string | null | undefined>
  parentId: ReadonlyRef<number | null | undefined>
  ancestorIds: ReadonlyRef<number[]>
  emitInput: (value: Array<number | null> | MultiselectOption[]) => void
}

/**
 * Adapts the generic select-all sentinel engine to the location picker model.
 *
 * Locations are scope-aware: the current picker state can represent all items
 * under a parent location, all items of a location type, or a searched subset.
 */
export function useLocationSelectAll({
  value,
  searchText,
  selectedLocationIds,
  items,
  showNoLocationOption,
  locationType,
  parentName,
  parentId,
  ancestorIds,
  emitInput,
}: UseLocationSelectAllArgs) {
  const visibleLocationItems = computed<LocationPickerItem[]>(() => {
    const currentItems = items.value as LocationPickerItem[]
    return showNoLocationOption.value
      ? [noLocationOption as LocationPickerItem, ...currentItems]
      : currentItems
  })

  const filteredOptions = computed<MultiselectOption[]>(() => {
    return visibleLocationItems.value.map(locationItemToOption)
  })

  /**
   * Checks whether a location item or sentinel option matches a text search.
   */
  function itemMatchesSearchText(item: LocationPickerItem | MultiselectOption, searchText: string) {
    const normalizedSearchText = searchText.toLowerCase()
    const title = 'title' in item ? item.title : item.label
    return title?.toLowerCase().includes(normalizedSearchText) ?? false
  }

  /**
   * Checks whether an item is inside the parent portion of a location scope.
   */
  function itemMatchesParentScope(
    item: LocationPickerItem | MultiselectOption,
    scope: SelectAllScope,
  ) {
    if (typeof scope.parentLocationId !== 'number') return true

    const itemParentId = (item as { parentId?: number | null }).parentId
    if (itemParentId === scope.parentLocationId) return true

    return (
      typeof itemParentId === 'number' &&
      itemParentId === parentId.value &&
      ancestorIds.value.includes(scope.parentLocationId)
    )
  }

  /**
   * Checks whether an item matches all dimensions of a location select-all scope.
   */
  function itemMatchesScope(item: LocationPickerItem | MultiselectOption, scope: SelectAllScope) {
    const itemLocationTypeId = (item as { locationTypeId?: number | null }).locationTypeId
    if (typeof scope.locationTypeId === 'number' && itemLocationTypeId !== scope.locationTypeId) {
      return false
    }

    if (!itemMatchesParentScope(item, scope)) {
      return false
    }

    if (scope.searchText && !itemMatchesSearchText(item, scope.searchText)) {
      return false
    }

    return true
  }

  /**
   * Checks whether a child scope is inside a selected parent scope.
   */
  function childScopeIsInsideParentScope(parentScope: SelectAllScope, childScope: SelectAllScope) {
    if (typeof parentScope.parentLocationId !== 'number') return true

    return (
      childScope.parentLocationId === parentScope.parentLocationId ||
      (childScope.parentLocationAncestorIds?.includes(parentScope.parentLocationId) ?? false)
    )
  }

  /**
   * Checks whether one location scope fully covers another location scope.
   */
  function scopeContains(parentScope: SelectAllScope, childScope: SelectAllScope) {
    if (
      typeof parentScope.locationTypeId === 'number' &&
      parentScope.locationTypeId !== childScope.locationTypeId
    ) {
      return false
    }

    if (
      typeof parentScope.parentLocationId === 'number' &&
      !childScopeIsInsideParentScope(parentScope, childScope)
    ) {
      return false
    }

    if (
      parentScope.searchText &&
      parentScope.searchText.toLowerCase() !== childScope.searchText?.toLowerCase()
    ) {
      return false
    }

    return true
  }

  /**
   * Builds the current location select-all scope from picker UI state.
   */
  function getCurrentSelectAllScope(): SelectAllScope | null {
    const trimmedSearchText = searchText.value?.trim() ?? ''
    const locationTypeId = locationType.value?.id
    const hasLocationType = typeof locationTypeId === 'number'
    const currentParentId = parentId.value
    const hasParent = typeof currentParentId === 'number'

    if (!trimmedSearchText && !hasLocationType && !hasParent) return null

    return {
      ...(trimmedSearchText ? { searchText: trimmedSearchText } : {}),
      ...(hasLocationType
        ? {
            locationTypeId,
            locationTypeLabel: locationType.value?.label,
          }
        : {}),
      ...(hasParent
        ? {
            parentLocationId: currentParentId,
            parentLocationLabel: parentName.value ?? undefined,
            ...(ancestorIds.value.length > 0
              ? { parentLocationAncestorIds: ancestorIds.value }
              : {}),
          }
        : {}),
    }
  }

  /**
   * Converts a picker item into the MultiselectOption shape stored by sentinels.
   */
  function locationItemToOption(item: LocationPickerItem): MultiselectOption {
    return {
      label: item.title ?? String(item.id),
      value: item.id as number,
      ...(typeof item.locationTypeId === 'number' ? { locationTypeId: item.locationTypeId } : {}),
      ...(typeof item.parentId === 'number' ? { parentId: item.parentId } : {}),
    } as MultiselectOption
  }

  /**
   * Resolves a normal-mode selected location ID back to a display option.
   */
  function resolveLocationOption(value: number | null): MultiselectOption {
    const item = visibleLocationItems.value.find((visibleItem) => visibleItem.id === value)
    if (item) {
      return locationItemToOption(item)
    }

    if (value === null) {
      return locationItemToOption(noLocationOption as LocationPickerItem)
    }

    return { label: String(value), value }
  }

  const sentinel = useSelectAllSentinel<LocationPickerItem, number | null>({
    value,
    searchText,
    selectedValues: selectedLocationIds,
    visibleItems: () => visibleLocationItems.value,
    getItemValue: (item) => item.id as number | null,
    itemToOption: locationItemToOption,
    resolveOption: resolveLocationOption,
    normalValueToKey: (value) => {
      if (typeof value === 'number' || value === null) {
        return value
      }
      return undefined
    },
    emitSelectAllValue: emitInput,
    emitNormalValue: emitInput,
    itemMatchesSearchText,
    searchSelectAllMode: 'sentinel',
    normalDeselectAllMode: 'visible-items',
    requireSearchScopeCoverage: false,
    getCurrentScope: getCurrentSelectAllScope,
    itemMatchesScope,
    scopeContains,
  })

  return {
    filteredOptions,
    visibleLocationItems,
    isSelectAllMode: sentinel.isSelectAllMode,
    isItemSelected: sentinel.isItemSelected,
    toggleItem: sentinel.toggleItem,
    selectAllFiltered: sentinel.selectAllFiltered,
    deselectAllFiltered: sentinel.deselectAllFiltered,
    areAllFilteredSelected: sentinel.areAllFilteredSelected,
  }
}
